<template>
    <div>
        <ConfirmDialog
            :is-open="showConfirmDialog"
            :data="confirmDialogData"
            @on-confirm="confirmDialogConfirm"
            @on-abort="confirmDialogClose"
        >
            <template #title>
                {{ confirmDialogData.title }}
            </template>
            <template #content>
                {{ confirmDialogData.content }}
            </template>
        </ConfirmDialog>
        <CampaignProductDialog
            :is-open="showCampaignProductDialog"
            :product-data="currentProduct"
            @on-save="saveCampaignProductDialog"
            @on-close="closeCampaignProductDialog"
        />
        <AddProductFileDialog
            :is-open="showAddFileProductDialog"
            :product-id="currentProductId"
            @on-close="closeAddProductFileDialog"
            @add-product-file="saveAddProductFileDialog"
        />
        <JoinOrderToCampaignDialog
            :is-open="showJoinOrderToCampaignDialog"
            :campaign-id="campaignId"
            @on-success="joinOrderToCampaignSuccess"
            @on-close="closeJoinOrderToCampaignDialog"
        />
        <v-card flat>
            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="12">
                        <v-btn
                            color="secondary"
                            medium
                            data-sentry-label="Dodaj produkt kampanii"
                            :disabled="!isEditAllowed"
                            @click="createCampaignProductDialog"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj produkt
                        </v-btn>
                        <v-btn
                            color="primary"
                            medium
                            class="ml-3"
                            data-sentry-label="Powiąż zamówienie"
                            :disabled="!isEditAllowed"
                            @click="openJoinOrderToCampaignDialog"
                        >
                            Powiąż zamówienie
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-data-table
                            :headers="productsHeaders"
                            :items="products"
                            dense
                            item-key="id"
                            :mobile-breakpoint="0"
                            no-data-text="Brak danych"
                            :items-per-page="17"
                            hide-default-footer
                            :single-expand="singleExpand"
                            :expanded.sync="expanded"
                            show-expand
                            :loading="loading"
                            class="elevation-1"
                        >
                            <template #item.name="{item}">
                                {{ item.name }}
                                <v-icon
                                    v-if="isProductReady(item)"
                                    small
                                    color="success"
                                >
                                    mdi-check
                                </v-icon>
                            </template>
                            <template #item.totalAmount="{item}">
                                {{ item.totalAmount }}
                            </template>
                            <template #item.jobNumber="{item}">
                                {{ parseEmptyValue(item.jobNumber) }}
                            </template>
                            <template #item.actions="{item}">
                                <div justify="end" class="pa-2 text-end">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Usuń"
                                                :disabled="!item.canRemoveFromJob || !isEditAllowed"
                                                :loading="removeCampaignProductFromJobLoading == item.id"
                                                @click="tryRemoveCampaignProductFromJob(item)"
                                                v-on="on"
                                            >
                                                <v-icon color="orange">
                                                    mdi-book-remove-multiple-outline
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Usuń ze zlecenia</span>
                                    </v-tooltip>
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Edytuj"
                                                :disabled="!isEditAllowed"
                                                v-on="on"
                                                @click="openEditCampaignProductDialog(item)"
                                            >
                                                <v-icon color="teal">
                                                    mdi-pencil
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edytuj</span>
                                    </v-tooltip>
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Usuń"
                                                :disabled="!isEditAllowed"
                                                v-on="on"
                                                @click="tryDeleteProduct(item)"
                                            >
                                                <v-icon color="pink">
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Usuń</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template #expanded-item="{headers, item}">
                                <td :colspan="headers.length" class="pt-4">
                                    <v-btn
                                        color="secondary"
                                        medium
                                        data-sentry-label="Dodaj plik produktu"
                                        :disabled="!isEditAllowed"
                                        @click="addProductFileDialog(item)"
                                    >
                                        <v-icon left>
                                            mdi-file-image-outline
                                        </v-icon>
                                        Dodaj plik
                                    </v-btn>
                                    <v-divider class="mt-4" />
                                    <template
                                        v-for="(file, index) in item.productFiles"
                                        min-width="100%"
                                        class="d-flex"
                                    >
                                        <v-row v-if="file.id === null" :key="index + 'loader'">
                                            <v-col cols="12" md="10">
                                                <v-skeleton-loader type="table-cell" />
                                            </v-col>
                                            <v-col class="justify align" cols="12" md="1">
                                                <v-skeleton-loader type="table-cell" />
                                            </v-col>
                                            <v-col class="justify align" cols="12" md="1">
                                                <v-skeleton-loader type="table-cell" />
                                            </v-col>
                                        </v-row>
                                        <v-row v-else :key="file.id">
                                            <v-col cols="12" md="6">
                                                <a :href="file.filePath" target="_blank" class="d-flex">
                                                    <v-icon 
                                                        color="primary"
                                                    >
                                                        mdi-file-image-outline
                                                    </v-icon>
                                                    <span class="pa-2 pb-0">
                                                        {{ file.fileName }} 
                                                    </span>
                                                </a>
                                            </v-col>
                                            <v-col class="justify align pa-2 text-end" cols="12" md="6">
                                                <v-chip
                                                    v-if="file.isAccepted"
                                                    color="green"
                                                    label
                                                    outlined
                                                >
                                                    ZAAKCEPTOWANE
                                                    <v-icon color="green" right>
                                                        mdi-check
                                                    </v-icon>
                                                </v-chip>
                                                <v-btn
                                                    v-else
                                                    class="ph-1"
                                                    color="green"
                                                    small
                                                    dark
                                                    @click="acceptProductFile(file)"
                                                >
                                                    Akceptuj
                                                </v-btn>
                                                <v-tooltip
                                                    :open-delay="tooltipDelay"
                                                    top
                                                >
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            :disabled="!isEditAllowed"
                                                            class="ml-3"
                                                            v-on="on"
                                                            @click="tryDeleteProductFile(file)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </td>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import { errorHandler } from '@/utils/errorHandler'
import tooltipDelay from '@/mixins/tooltip'
import CampaignProductDialog from '@/views/Campaigns/Dialogs/CampaignProductDialog'
import AddProductFileDialog from '@/views/Campaigns/Dialogs/AddProductFileDialog'
import JoinOrderToCampaignDialog from '@/views/Campaigns/Dialogs/JoinOrderToCampaignDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import parse from '@/mixins/parse'

export default {
    name: 'ProductsList',
    components: {
        CampaignProductDialog,
        AddProductFileDialog,
        JoinOrderToCampaignDialog,
        ConfirmDialog,
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse,
    ],
    props: {
        products: {
            type: Array,
            required: true,
        },
        campaignId: {
            type: Number,
            required: true,
        },
        isEditAllowed: {
            type: Boolean,
        },
        loading: {
            type: Boolean,
        },
    },
    data() {
        return {
            currentProduct: null,
            expanded: [],
            singleExpand: false,
            showCampaignProductDialog: false,
            showAddFileProductDialog: false,
            showJoinOrderToCampaignDialog: false,
            removeCampaignProductFromJobLoading: false,
            productsHeaders: [
                {
                    text: 'Nazwa',
                    value: 'name',
                },
                {
                    text: 'Nr zlecenia',
                    value: 'jobNumber',
                },
                {
                    text: 'Format',
                    value: 'format',
                    align: 'center',
                    sortable: false,
                    width: 120,
                },
                {
                    text: 'Ilość',
                    value: 'totalAmount',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 170,
                },
            ],
            showConfirmDialog: false,
            confirmDialogData: {
                confirm: () => {},
            },
        }
    },
    computed: {
        currentProductId() {
            return this.currentProduct?.id || null
        },
    },
    methods: {
        deleteCampaignProduct({ data }) {
            this.$store.dispatch('campaigns/deleteCampaignProduct', data.id)
                .then(() => {
                    this.$emit('product-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Produkt kampanii został usunięty.'
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createCampaignProductDialog() {
            this.currentProduct = null
            this.showCampaignProductDialog = true
        },
        openEditCampaignProductDialog(product) {
            this.currentProduct = product
            this.showCampaignProductDialog = true
        },
        openJoinOrderToCampaignDialog() {
            this.showJoinOrderToCampaignDialog = true
        },
        saveCampaignProductDialog(product) {
            if (this.currentProduct) {
                this.updateProduct(product)
            } else {
                this.createProduct(product)
            }
        },
        updateProduct(product) {
            this.$store.dispatch('campaigns/updateCampaignProduct', {
                ...this.currentProduct,
                ...product
            })
                .then(() => {
                    this.$emit('product-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Produkt kampanii został zaktualizowany.'
                        })
                    })
                })
                .finally(() => {
                    this.closeCampaignProductDialog()
                })
        },
        createProduct(product) {
            this.$store.dispatch('campaigns/createCampaignProduct', {
                ...product,
                campaignId: this.campaignId,
            })
                .then(() => {
                    this.$emit('product-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Produkt kampanii został dodany.'
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeCampaignProductDialog()
                })
        },
        closeCampaignProductDialog() {
            this.showCampaignProductDialog = false
        },
        addProductFileDialog(product) {
            this.currentProduct = product
            this.showAddFileProductDialog = true
        },
        saveAddProductFileDialog() {
            this.currentProduct.productFiles.push({id: null})
            this.$emit('product-changed')
        },
        closeAddProductFileDialog() {
            this.currentProduct = null
            this.showAddFileProductDialog = false
        },
        deleteProductFile({ data }) {
            this.$store.dispatch('campaigns/deleteProductFile', data.id)
                .then(() => {
                    this.$emit('product-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Plik produktu został usunięty.'
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        acceptProductFile(file) {
            this.$store.dispatch('campaigns/acceptProductFile', { ...file, isAccepted: true })
                .then(() => {
                    file.isAccepted = true
                    this.$emit('product-changed')
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        joinOrderToCampaignSuccess() {
            this.$emit('product-changed')
        },
        removeCampaignProductFromJob({ data }) {
            this.removeCampaignProductFromJobLoading = data.id
            this.$store.dispatch('campaigns/removeCampaignProductFromJob', {
                orderProductId: data.orderProductId,
                jobNumber: data.jobNumber,
            })
                .then(() => {
                    this.$emit('product-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Produkt został usunięty ze zlecenia.'
                        })
                        this.removeCampaignProductFromJobLoading = false
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        tryRemoveCampaignProductFromJob(product) {
            this.confirmDialogData = {
                title: 'Usuwanie produktu ze zlecenia',
                content: 'Czy na pewno chcesz usunąć produkt ze zlecenia?',
                confirm: this.removeCampaignProductFromJob,
                data: product,
            }
            this.showConfirmDialog = true
        },
        tryDeleteProduct(product) {
            this.confirmDialogData = {
                title: 'Usuwanie produktu',
                content: 'Czy na pewno chcesz usunąć produkt?',
                confirm: this.deleteCampaignProduct,
                data: product,
            }
            this.showConfirmDialog = true
        },
        tryDeleteProductFile(file) {
            this.confirmDialogData = {
                title: 'Usuwanie pliku',
                content: 'Czy na pewno chcesz usunąć plik?',
                confirm: this.deleteProductFile,
                data: file,
            }
            this.showConfirmDialog = true
        },
        isProductReady(product) {
            return product.totalAmount &&
                product.productTemplateId &&
                product.productFiles.length &&
                product.amountLeftToOrder &&
                product.productFiles.every(file => file.isAccepted)
        },
        confirmDialogConfirm(payload) {
            this.confirmDialogData.confirm(payload)
            this.confirmDialogClose()
        },
        confirmDialogClose() {
            this.showConfirmDialog = false
            setTimeout(() => {
                this.confirmDialogData = {
                    title: null,
                    content: null,
                    confirm: () => {},
                    data: null,
                }
            }, 500)
        },
        closeJoinOrderToCampaignDialog() {
            this.showJoinOrderToCampaignDialog = false
        },
    },
}
</script>